import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { z } from "zod";
import * as stylex from "@stylexjs/stylex";
import { useSession } from "@/lib/auth-client";
import { useDocumentTitle } from "@/lib/window-title";
import Button from "@/components/ui/Button";
import CenteredCard from "@/components/ui/CenteredCard";
import { LoadingRegion, Skeleton } from "@/components/ui/Skeleton";
import "@/design/tokens.stylex";
import { colors, space, text } from "@/design/tokens.stylex";
const styles = {
  card: {
    kOIVth: "x1l51sop",
    $$css: true
  }
};

// OAuth consent for the MCP endpoint (ADR 0011). /api/oauth/authorize validated
// the client and sent the person here with a pending request id bound to their
// session. Accept or deny posts the decision back and follows the redirect the
// server returns; nothing here trusts the URL beyond that id.
const consentSearchSchema = z.object({
  request: z.string().catch("")
});
export const Route = createFileRoute("/oauth/consent")({
  validateSearch: consentSearchSchema,
  component: ConsentPage
});
interface PendingConsent {
  requestId: string;
  client: {
    id: string;
    name: string;
    host: string;
    uri: string | null;
  };
  redirectHost: string;
  scope: string[];
  workspaces: {
    slug: string;
    name: string;
  }[];
}
const SCOPE_TEXT: Record<string, string> = {
  "mcp:read": "Read reporting, setup and memory in every Company you can access",
  "mcp:write": "Make changes you confirm in the chat, with your own permissions",
  offline_access: "Keep access until you revoke it under Settings › Connected apps"
};
function ConsentPage() {
  const {
    request
  } = Route.useSearch();
  useDocumentTitle({
    section: "Authorize"
  });
  const {
    data: session,
    isPending
  } = useSession();
  const [submitting, setSubmitting] = useState<"accept" | "deny" | null>(null);
  const [error, setError] = useState<string | null>(null);
  // A client asking to write may be narrowed to read only; one asking only to
  // read is never offered more.
  const [write, setWrite] = useState(true);
  const pending = useQuery({
    queryKey: ["oauth-consent", request],
    enabled: Boolean(request) && Boolean(session),
    retry: false,
    queryFn: async () => {
      const response = await fetch(`/api/oauth/consent?request=${encodeURIComponent(request)}`, {
        credentials: "include"
      });
      const body = (await response.json()) as PendingConsent & {
        error_description?: string;
      };
      if (!response.ok) throw new Error(body.error_description ?? "This request is no longer valid.");
      return body;
    }
  });
  if (!isPending && !session) {
    window.location.href = `/login?next=${encodeURIComponent(`/oauth/consent?request=${request}`)}`;
    return null;
  }
  async function decide(accept: boolean) {
    setSubmitting(accept ? "accept" : "deny");
    setError(null);
    try {
      const response = await fetch("/api/oauth/consent", {
        method: "POST",
        headers: {
          "content-type": "application/json"
        },
        credentials: "include",
        body: JSON.stringify({
          requestId: request,
          accept,
          write
        })
      });
      const body = (await response.json()) as {
        location?: string;
        error_description?: string;
      };
      if (!response.ok || !body.location) throw new Error(body.error_description ?? `Could not complete the request (${response.status})`);
      window.location.href = body.location;
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong");
      setSubmitting(null);
    }
  }
  const client = pending.data?.client;
  const asksToWrite = pending.data?.scope.includes("mcp:write") ?? false;
  return <CenteredCard width="lg" xstyle={styles.card}>
      <img src="/logo.svg" alt="A&amp;CO" className="x1lliihq xxk0z11 x14atkfc xqcrz7y" />
      <h1 className="x1ghz6dp xuxpmqx xk50ysn xwn7fz2 xgwkh9f">
        Allow {client?.name ?? "an application"} to use Growth Engine?
      </h1>
      {pending.error && <p className="x1ghz6dp xtu3avg x1nif09i" role="alert">
          {pending.error.message}
        </p>}
      {pending.isLoading && <LoadingRegion label="Loading the request">
          <Skeleton width="90%" />
          <Skeleton width="70%" />
          <Skeleton width="80%" />
        </LoadingRegion>}
      {pending.data && <>
          <p className="x1ghz6dp xdj3nnt x1evy7pa x1e3avs9">
            <strong className="xk50ysn xgwkh9f">{client!.name}</strong> at{" "}
            <code className="x71xnqm x7x6yu3 xgwkh9f">{client!.host}</code> will act as you. After you
            approve, you will be sent back to{" "}
            <code className="x71xnqm x7x6yu3 xgwkh9f">{pending.data.redirectHost}</code>.
          </p>
          <ul className="x1ghz6dp x1mtild6 xdj3nnt x1dbl2gt xgwkh9f" aria-label="Permissions">
            {pending.data.scope.filter(scope => scope !== "mcp:write" || write).map(scope => <li key={scope}>{SCOPE_TEXT[scope] ?? scope}</li>)}
          </ul>
          {asksToWrite && <fieldset className="x78zum5 xdt5ytf xsuxsxl x1ghz6dp x1717udv xc342km">
              <legend className="x1ghz6dp x7x6yu3 xk50ysn x1e3avs9">Access</legend>
              <label className="x78zum5 x6s0dn4 x166cprz xdj3nnt xgwkh9f">
                <input type="radio" name="access" checked={write} onChange={() => setWrite(true)} />
                Read and change
              </label>
              <label className="x78zum5 x6s0dn4 x166cprz xdj3nnt xgwkh9f">
                <input type="radio" name="access" checked={!write} onChange={() => setWrite(false)} />
                Read only
              </label>
            </fieldset>}
          <div className="x78zum5 xdt5ytf xsuxsxl">
            <p className="x1ghz6dp x7x6yu3 xk50ysn x1e3avs9">It will reach these Companies, and only these</p>
            <ul className="x1ghz6dp x1mtild6 xdj3nnt x1dbl2gt xgwkh9f" aria-label="Companies">
              {pending.data.workspaces.map(workspace => <li key={workspace.slug}>{workspace.name}</li>)}
              {pending.data.workspaces.length === 0 && <li>None yet</li>}
            </ul>
          </div>
          <p className="x1ghz6dp x7x6yu3 x1i6qc9y">
            Signed in as {session?.user.email}. It never gets a password, and you can revoke it any
            time under Settings › Connected apps.
          </p>
          {error && <p className="x1ghz6dp xtu3avg x1nif09i" role="alert">
              {error}
            </p>}
          <div className="x78zum5 x13a6bvl x166cprz xfisy6v">
            <Button variant="ghost" disabled={submitting !== null} onClick={() => decide(false)}>
              {submitting === "deny" ? "Denying…" : "Deny"}
            </Button>
            <Button variant="primary" disabled={submitting !== null} onClick={() => decide(true)}>
              {submitting === "accept" ? "Allowing…" : "Allow"}
            </Button>
          </div>
        </>}
    </CenteredCard>;
}